import { gql } from '@apollo/client/core'
import { CategoryDto, CategoryReorderInput, CreateCategoryDto, UpdateCategoryDto } from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `CategoryService`.
 *
 * The only member beyond plain CRUD is `reorder`, which the admin category tree calls after a drag —
 * `category.service.interface.ts` existed to declare it and nothing else.
 */

const crud = useBaseCrud<CategoryDto>('category')

const categoryFragments = {
  // Every locale ships on every category rather than the API localising `name`: the list is
  // fetched once and cached, so a locale-scoped name would leave the categories in the previous
  // language until a reload when the reader changes it in the picker.
  category: gql`
    fragment CategoryFragment on CategoryDto {
      id
      name
      sortOrder
      parentCategoryId
      translations {
        locale
        name
      }
      parentCategory {
        id
        name
        sortOrder
      }
      childCategories {
        id
        name
        parentCategoryId
        sortOrder
        translations {
          locale
          name
        }
      }
    }
  `
}

const get = (): Promise<CategoryDto[]> => {
  return useApollo().query({
    query: gql`
      query GetCategories {
        categories {
          ...CategoryFragment
        }
      }
      ${categoryFragments.category}
    `
  })
}

const getById = (id?: number): Promise<CategoryDto> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query GetCategory (
        $id: Int!
      ) {
        category (id: $id) {
          ...CategoryFragment
        }
      }
      ${categoryFragments.category}
    `
  })
}

const create = (model: CreateCategoryDto): Promise<CategoryDto> => {
  return useApollo().mutate({
    variables: {
      ...model
    },
    mutation: gql`
      mutation CreateCategory (
        $name: String!
        $parentCategoryId: Int
      ) {
        createCategory (name: $name, parentCategoryId: $parentCategoryId) {
          ...CategoryFragment
        }
      }
      ${categoryFragments.category}
    `
  })
}

const update = (model: UpdateCategoryDto): Promise<CategoryDto> => {
  return useApollo().mutate({
    variables: {
      ...model
    },
    mutation: gql`
      mutation UpdateCategory (
        $id: Int!
        $name: String
        $parentCategoryId: Int
        $sortOrder: Int
      ) {
        updateCategory (id: $id, name: $name, parentCategoryId: $parentCategoryId, sortOrder: $sortOrder) {
          ...CategoryFragment
        }
      }
      ${categoryFragments.category}
    `
  })
}

const reorder = (items: CategoryReorderInput[]): Promise<CategoryDto[]> => {
  return useApollo().mutate({
    variables: {
      items
    },
    mutation: gql`
      mutation ReorderCategories (
        $items: [CategoryReorderInput!]!
      ) {
        reorderCategories (items: $items) {
          ...CategoryFragment
        }
      }
      ${categoryFragments.category}
    `
  })
}

const deleteCategory = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation DeleteCategory (
        $id: Int!
      ) {
        deleteCategory(id: $id) {
          id
        }
      }
    `
  })
}

/**
 * `globalLoader` is chained by the list mixin, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  create,
  update,
  reorder,
  delete: deleteCategory,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function categoryService () {
  return service
}
